import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { BILLING_SEARCH_PLACEHOLDER } from '@/features/billing/constants/billing.constants';
import { getApprovalStatCards } from '@/features/billing/utils/billingStatCards';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { dcrApproveInfiniteQueryOptions } from '../api/dcrApprove.api';
import { DcrApproveFilterPanel } from '../components/DcrApproveFilterPanel';
import { getDcrApproveColumns } from '../components/DcrApproveColumns';
import { useDcrApproveFilters } from '../hooks/useDcrApproveFilters';

export function DcrApprovePage() {
  const controller = useDcrApproveFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(dcrApproveInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { search } = controller.params;
  const columns = useMemo(() => getDcrApproveColumns(search), [search]);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR Approve"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DcrApproveFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the DCRs"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={getApprovalStatCards('dcrApprove', firstPageMeta)}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="zmName"
            emptyMessage="No DCRs for these filters"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
